"use client";

import { useEffect, useRef } from "react";

const GOOGLE_GSI_SCRIPT = "https://accounts.google.com/gsi/client";

interface GoogleSignInProps {
  onSuccess: (idToken: string) => void;
  onError?: (error: string) => void;
  text?: "signin_with" | "signup_with" | "continue_with";
}

// Track global script loading
let gsiLoaded = false;
let gsiLoading = false;
const gsiCallbacks: (() => void)[] = [];

function loadGsiScript(): Promise<void> {
  if (gsiLoaded) return Promise.resolve();

  return new Promise((resolve) => {
    if (gsiLoading) {
      gsiCallbacks.push(resolve);
      return;
    }
    gsiLoading = true;

    const script = document.createElement("script");
    script.src = GOOGLE_GSI_SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      gsiLoaded = true;
      gsiLoading = false;
      resolve();
      gsiCallbacks.forEach((cb) => cb());
      gsiCallbacks.length = 0;
    };
    script.onerror = () => {
      gsiLoading = false;
      resolve();
    };
    document.head.appendChild(script);
  });
}

export default function GoogleSignIn({
  onSuccess,
  onError,
  text = "continue_with",
}: GoogleSignInProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const callbacksRef = useRef({ onSuccess, onError });
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

  // Keep callbacks fresh without triggering useEffect re-run
  useEffect(() => {
    callbacksRef.current = { onSuccess, onError };
  });

  useEffect(() => {
    if (!clientId || !buttonRef.current) return;

    let mounted = true;

    loadGsiScript().then(() => {
      if (!mounted || !buttonRef.current) return;
      if (typeof window.google === "undefined") return;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential: string }) => {
          if (response.credential) {
            callbacksRef.current.onSuccess(response.credential);
          } else {
            callbacksRef.current.onError?.("Google Sign-In failed — no credential received.");
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text,
        shape: "rectangular",
        logo_alignment: "left",
        width: 360,
      });
    });

    return () => {
      mounted = false;
    };
  }, [clientId, text]);

  if (!clientId) return null;

  return <div ref={buttonRef} className="flex justify-center" />;
}

// TypeScript declarations for Google Identity Services
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (
            element: HTMLElement,
            options: Record<string, unknown>
          ) => void;
          prompt: () => void;
          revoke: (email: string, callback: () => void) => void;
        };
      };
    };
  }
}
